import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import isEmail from 'validator/es/lib/isEmail';
import isURL from 'validator/es/lib/isURL';
import isEmpty from 'validator/es/lib/isEmpty';
import config from '../config';
import ReactGA from "react-ga4";

const propTypes = {
  onSignupClose: PropTypes.func.isRequired,
  showSignup: PropTypes.bool.isRequired,
  signUpMessage: PropTypes.object.isRequired,
  initialEmail: PropTypes.string,
  initialWebsite: PropTypes.string,
};

const revenueOptions = [
  'Less than $1,000 per month',
  '$1,000 - $5,000 per month',
  '$5,000 - $10,000 per month',
  '$10,000 - $20,000 per month',
  'More than $20,000 per month'
];

export default function Signup({onSignupClose, showSignup, signUpMessage, initialEmail = '', initialWebsite = '' }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState(initialEmail);
  const [website, setWebsite] = useState(initialWebsite);
  const [revenue, setRevenue] = useState('');
  const [errors, setErrors] = useState({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const onNameChange = (event) => {
    setName(event.target.value);
  };

  const onEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const onWebsiteChange = (event) => {
    setWebsite(event.target.value);
  };

  const onRevenueChange = (event) => {
    setRevenue(event.target.value);
  };

  const validateWebsite = async () => {
    const isValid = isURL(website);
    setIsButtonDisabled(true);
    if(isValid)
      await checkWebsiteValidation(website);
    else
      setErrors(prev => ({...prev, 'website': true}));
  };

  const validateEmail = () => {
    const isValid = isEmail(email);
    setErrors(prev => ({...prev, 'email': !isValid}));
    return isValid;
  };

  const validateNotEmpty = (field, value) => {
    const isValid = !isEmpty(value);
    setErrors(prev => ({...prev, [field]: !isValid}));
    return isValid;
  };

  const checkWebsiteValidation = async (uri) => {
    let res = await fetch(`${config.web_app_url}grader/validate-url?website=${uri}`);
    res.text().then(function (text) {
      if (text === "success") {
        setIsButtonDisabled(false)
        setErrors(prev => ({...prev, 'website': false}));
        const urlParams = new URLSearchParams(window.location.search);
        const testMode=urlParams.get('testMode');
        if(testMode){
          fetch(`${config.pgai_bot_url}&url=${uri}`);
        }
        else{
          fetch(`${config.pgai_bot_url}?url=${uri}`);
        }
      }
      else
        setErrors(prev => ({...prev, 'website': true}));
    });
  }
  
  const onWebsiteBlur = async () => {
    await validateWebsite();
  };

  const onSubmit = async (e) => {
    setErrors({}); // remove errors before validating

    if (!validateNotEmpty('name', name) ||
      !validateEmail() ||
      !validateWebsite() ||
      !validateNotEmpty('revenue', revenue)) {
        e.preventDefault();
        ReactGA.event('convert_custom', {
          category: 'Web Grader',
          label: 'Web Grader - Sign up button clicked but form submission prevented',
        });
        return
    }
    ReactGA.event('convert_custom', {
      category: 'Web Grader',
      label: 'Web Grader - Sign up Combined All',
    });
    if (revenue === '$10,000 - $20,000 per month' || revenue === 'More than $20,000 per month') {
      ReactGA.event('convert_custom', {
        category: 'Web Grader',
        label: 'Web Grader - Sign up Web Grader',
      });
    }
  }

  return <Dialog fullWidth maxWidth='xs' onClose={onSignupClose} open={showSignup}>
    <Box component="form" method="POST" action={config.signup_api_url} noValidate onSubmit={onSubmit} sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem'}} gutterBottom>{signUpMessage.title}</Typography>
      <Typography><br />{signUpMessage.message}</Typography>
      <TextField
        margin="normal"
        required
        fullWidth
        name="name"
        type="text"
        id="name"
        label="Full Name"
        value={name}
        onChange={onNameChange}
        error={errors.name}
        helperText={errors.name ? "Please Enter Your Name." : ""}
        sx={{
          mt: 4,
        }}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="email"
        type="email"
        id="email"
        label="Email"
        value={email}
        onChange={onEmailChange}
        error={errors.email}
        helperText={errors.email ? "Please Enter a valid Email." : ""}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="website"
        type="text"
        id="website"
        label="Website"
        value={website}
        onChange={onWebsiteChange}
        error={errors.website}
        helperText={errors.website ? "Please Enter a valid website URL." : ""}
        onBlur={onWebsiteBlur}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        select
        value={revenue}
        onChange={onRevenueChange}
        id="monthly_revenue"
        name="monthly_revenue"
        label="Your Monthly Ad Revenue (of all sites)"
        error={errors.revenue}
        helperText={errors.revenue ? "Please Select Revenue." : ""}
      >
        {revenueOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      <input
        name="ccsrft"
        type="hidden"
        value={window.sspg_prod ? window.sspg_prod.data.ccsrft : ''}
      />
      <Typography sx={{ mb: 2, fontSize: 12 }} color="text.secondary">
        This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
      </Typography>
      <Typography sx={{ mb: 2, fontSize: 12 }} color="text.secondary">
        By signing up, you agree to our Privacy Policy.
      </Typography>
      <Box sx={{ textAlign: 'center' }}>
        <Button
            id="btn-sign-up"
            type="submit"
            variant="contained"
            disabled={isButtonDisabled}
            sx={{
              mb: 2,
              mx: 'auto',
              px: 3,
              py: 1,
              backgroundColor: '#FEC129',
              color: '#4A4C4D',
              fontWeight: 600,
              fontSize: 15,
              '&:hover': {
                backgroundColor: '#D9A91B',
              },
          }}
          >
          SIGN UP
        </Button>
      </Box>
      <Typography sx={{ mb: 2, fontSize: 12 }} color="text.secondary">
        No credit card required. Cancel any time.
      </Typography>
    </Box>
  </Dialog>;
}

Signup.propTypes = propTypes;